import type { ProviderSetup } from '@epilot/sdk/phone-integration'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { getIdentityClient } from '@/api/identity-client'
import { getPhoneIntegrationClient } from '@/api/phone-integration-client'

/** identity-api partner key of the Babelforce partner applications (one per stage) */
const BABELFORCE_PARTNER_KEY = 'babelforce'

const providerSetupKey = (orgId: string) =>
  ['phone-integration', 'provider', orgId] as const
const providerSetupsKey = ['phone-integration', 'provider-setups'] as const

/** Every organization with a provider tenant, for the overview (no live SCIM or SSO reads). */
export const useProviderSetups = () =>
  useQuery({
    queryKey: providerSetupsKey,
    queryFn: async () =>
      (await getPhoneIntegrationClient().listProviderSetups()).data.results,
    retry: false,
    refetchOnWindowFocus: false
  })

export const useProviderSetup = (orgId: string) =>
  useQuery({
    queryKey: providerSetupKey(orgId),
    queryFn: async () =>
      (await getPhoneIntegrationClient().getProviderSetup({ org_id: orgId }))
        .data,
    enabled: Boolean(orgId),
    retry: false,
    refetchOnWindowFocus: false
  })

/**
 * Stores the state a setup run returned (it never contains the secret that was sent) and marks the
 * overview stale, since the run may have created the tenant or changed its status.
 */
export const useStoreProviderSetup = () => {
  const queryClient = useQueryClient()

  return (setup: ProviderSetup) => {
    queryClient.setQueryData(providerSetupKey(setup.org_id), setup)
    void queryClient.invalidateQueries({ queryKey: providerSetupsKey })
  }
}

export const useBabelforceApplications = () =>
  useQuery({
    queryKey: ['identity', 'clients', 'partner', BABELFORCE_PARTNER_KEY],
    /** The stage's active Babelforce applications, one of which SSO is pointed at. */
    queryFn: async () =>
      (
        await getIdentityClient().listClients({
          partner_key: BABELFORCE_PARTNER_KEY,
          status: 'active',
          size: 100
        })
      ).data.results,
    retry: false,
    refetchOnWindowFocus: false
  })
